import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import FormGroup from '../../wrappers/form_group';
import Select from '../../select';
import Icon from '../../../icon';
import { getCountryData } from '../../../../services/country_data';
import errorHandler from '../../../../services/error_handler';
import { fetchMapboxCountries } from '../../../../services/mapbox';
import { formatMapboxCountryRecordForSelectOpts } from '../../../../services/mapbox/formatters';
import { isBlank, isObjectWithLength } from '../../../../utility/types';
import typography from '../../../../styles/global_ui/typography.css';
import styles from './form_multi_country_select.css';
import { DEFAULT_COUNTRY_SELECT_OBJ, formatMapboxResults } from '../country_select';

const FormMultiCountrySelect = ({
  errors,
  helperText,
  label,
  name,
  countryCodes = [],
  onChange,
  disabled,
  placeholder = 'Start typing to select countries...',
}) => {
  // Display country names in the select dropdown
  const selectedCountriesData = useMemo(() => {
    return countryCodes.map(countryCode => {
      const { name: label } = getCountryData(countryCode) || {};
      return {
        label: label || countryCode,
        value: countryCode
      };
    });
  }, [countryCodes]);

  const handleAddCountry = useCallback((countryOption) => {
    if (!isObjectWithLength(countryOption) || countryCodes.includes(countryOption.value)) return;

    const newCountries = [...countryCodes, countryOption.value];
    onChange(newCountries);
  }, [countryCodes, onChange]);

  const handleRemoveCountry = useCallback((countryCodeToRemove) => {
    const newCountries = countryCodes.filter(code => code !== countryCodeToRemove);
    onChange(newCountries);
  }, [countryCodes, onChange]);

  const getCountryOptions = useCallback((query) => fetchMapboxCountries(query)
    .then((res) => formatMapboxResults(res))
    .catch((err) => errorHandler('FormMultiCountrySelect getCountryOptions:', err)), []);

  return (
    <FormGroup
      errors={Array.isArray(errors) ? errors.join(', ') : errors}
      helperText={helperText}
      label={label}
      name={name}
      uuid="multi-country-search"
      value=""
    >
      <div>
        <Select
          asyncOpts={{
            initOnMount: true,
            request: getCountryOptions,
          }}
          disabled={disabled}
          hasErrors={errors?.length > 0}
          onSelectedChange={handleAddCountry}
          placeholder={placeholder}
          uiOpts={{ renderX: true }}
          uuid="multi-country-search"
          value={DEFAULT_COUNTRY_SELECT_OBJ}
        />

        {selectedCountriesData.length > 0 && (
          <div className={styles.selectedCountriesContainer}>
            <div className={styles.selectedCountriesLabel}>
              Selected Countries:
            </div>
            <div className={styles.selectedCountriesList}>
              {selectedCountriesData.map((country) => (
                <div key={country.value} className={styles.countryList}>
                  <span className={`${typography.bodyS} ${typography.bold} ${styles.countryLabel}`}>
                    {country.label}
                  </span>
                  {!disabled && (
                    <span onClick={() => handleRemoveCountry(country.value)} className={styles.removeButton}>
                      <Icon name="close" className={styles.removeIcon} />
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </FormGroup>
  );
};

FormMultiCountrySelect.propTypes = {
  countryCodes: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  errors: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  helperText: PropTypes.string,
  label: PropTypes.node,
  name: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
};

FormMultiCountrySelect.defaultProps = {
  countryCodes: [],
  disabled: false,
  errors: null,
  helperText: null,
  label: 'Countries',
  name: null,
  onChange: () => {},
  placeholder: 'Start typing to select countries...',
};

export default FormMultiCountrySelect;